import * as React from 'react';

import {
  Button,
  cn,
  Col,
  ContentSection,
  LocalizedDate,
  Money,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2 } from 'lucide-react';

import {
  $payment,
  $remotePaymentDetail,
  $removePaymentFromBill,
  $removePaymentFromInvoice,
} from '../../_state/paymentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { LinkedBillRecord, LinkedInvoiceRecord } from '../../types';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';

import { PaymentApplyToBillDialog } from './PaymentApplyToBillDialog';
import { PaymentApplyToInvoiceDialog } from './PaymentApplyToInvoiceDialog';

type LinkedDocumentRecord = LinkedBillRecord | LinkedInvoiceRecord;

const GRID_COLS = 'grid grid-cols-[2fr_1fr_1fr_50px] items-center gap-4';

type LinkedDocumentTableProps = {
  items: LinkedDocumentRecord[];
  columnLabel: string;
  appliedAmountLabel: string;
  appliedAtLabel: string;
  emptyStateLabel: string;
  onRemove: (id: string) => void;
  removeAriaLabel: string;
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ LINKED DOCUMENT TABLE ⚛️ ⚛️ ⚛️
//
const LinkedDocumentTable: React.FC<LinkedDocumentTableProps> = ({
  items,
  columnLabel,
  appliedAmountLabel,
  appliedAtLabel,
  emptyStateLabel,
  onRemove,
  removeAriaLabel,
  disabled,
}) => {
  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-neutral/60">{emptyStateLabel}</div>
    );
  }

  return (
    <Col className="@LinkedDocumentTable">
      <div
        className={cn(
          GRID_COLS,
          'border-b border-neutral/10 pb-2 text-sm font-medium text-neutral/60',
        )}
      >
        <div>{columnLabel}</div>
        <div>{appliedAmountLabel}</div>
        <div>{appliedAtLabel}</div>
        <div />
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(GRID_COLS, 'border-b border-neutral/5 py-3')}
        >
          <div className="font-medium">{item.documentNumber}</div>
          <div>
            {item.appliedAmount ? (
              <Money {...item.appliedAmount} />
            ) : (
              <>&ndash;</>
            )}
          </div>
          <div>
            {item.appliedAt ? (
              <LocalizedDate date={item.appliedAt} dateStyle="short" />
            ) : (
              <>&ndash;</>
            )}
          </div>
          <div>
            <Button
              variant="outline"
              color="failure"
              icon={<LucideTrash2 />}
              onPress={() => onRemove(item.id)}
              isDisabled={disabled}
              aria-label={removeAriaLabel}
            />
          </div>
        </div>
      ))}
    </Col>
  );
};

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ PAYMENT TAB LINKED DOCUMENTS ⚛️ ⚛️ ⚛️
//
export const PaymentTabLinkedDocuments: React.FC<Props> = ({ disabled }) => {
  // Translations
  const t = useTranslations();

  // States
  const [showApplyToBillDialog, setShowApplyToBillDialog] =
    React.useState(false);
  const [showApplyToInvoiceDialog, setShowApplyToInvoiceDialog] =
    React.useState(false);

  // Atoms
  const queryResult = useAtomValue($remotePaymentDetail);
  const payment = useAtomValue($payment);
  const removeFromBill = useSetAtom($removePaymentFromBill);
  const removeFromInvoice = useSetAtom($removePaymentFromInvoice);

  // Data hooks
  const toast = useToast();

  const refetch = queryResult?.refetch;
  const linkedBills = payment?.linkedBills ?? [];
  const linkedInvoices = payment?.linkedInvoices ?? [];
  const canApplyToBill =
    payment?.paymentType === 'Bill' || payment?.paymentType === 'AdvanceOut';
  const canApplyToInvoice =
    payment?.paymentType === 'Invoice' || payment?.paymentType === 'AdvanceIn';

  // Callbacks
  const handleRemoveFromBill = React.useCallback(
    async (billId: string) => {
      if (!payment?.id) return;
      try {
        await removeFromBill({ paymentId: payment.id, billId });
        toast.success(t.payments.detail.messages.paymentRemoved);
        refetch?.();
      } catch (err) {
        toast.error(
          t.payments.detail.messages.removeError(
            err instanceof Error ? err.message : t.messages.common.unknownError,
          ),
        );
      }
    },
    [payment?.id, removeFromBill, toast, refetch, t],
  );

  const handleRemoveFromInvoice = React.useCallback(
    async (invoiceId: string) => {
      if (!payment?.id) return;
      try {
        await removeFromInvoice({ paymentId: payment.id, invoiceId });
        toast.success(t.payments.detail.messages.paymentRemoved);
        refetch?.();
      } catch (err) {
        toast.error(
          t.payments.detail.messages.removeError(
            err instanceof Error ? err.message : t.messages.common.unknownError,
          ),
        );
      }
    },
    [payment?.id, removeFromInvoice, toast, refetch, t],
  );

  return (
    <FinanceDetailLayout className="@PaymentTabLinkedDocuments h-auto min-h-60">
      {(canApplyToBill || linkedBills.length > 0) && (
        <ContentSection
          title={
            <Row xBetween yCenter>
              <h3 className="text-lg font-semibold text-neutral/90">
                {t.payments.detail.linkedDocuments.linkedBills}
              </h3>
              {!disabled && canApplyToBill && (
                <Button
                  variant="outline"
                  icon={<LucidePlusCircle className="size-4" />}
                  onPress={() => setShowApplyToBillDialog(true)}
                >
                  {t.payments.detail.actions.applyToBill}
                </Button>
              )}
            </Row>
          }
        >
          <LinkedDocumentTable
            items={linkedBills}
            columnLabel={t.payments.detail.linkedDocuments.billNumber}
            appliedAmountLabel={t.payments.detail.linkedDocuments.appliedAmount}
            appliedAtLabel={t.payments.detail.linkedDocuments.appliedAt}
            emptyStateLabel={t.payments.detail.linkedDocuments.emptyState}
            onRemove={handleRemoveFromBill}
            removeAriaLabel={t.payments.detail.linkedDocuments.removeBill}
            disabled={disabled}
          />
        </ContentSection>
      )}

      {(canApplyToInvoice || linkedInvoices.length > 0) && (
        <ContentSection
          title={
            <Row xBetween yCenter>
              <h3 className="text-lg font-semibold text-neutral/90">
                {t.payments.detail.linkedDocuments.linkedInvoices}
              </h3>
              {!disabled && canApplyToInvoice && (
                <Button
                  variant="outline"
                  icon={<LucidePlusCircle className="size-4" />}
                  onPress={() => setShowApplyToInvoiceDialog(true)}
                >
                  {t.payments.detail.actions.applyToInvoice}
                </Button>
              )}
            </Row>
          }
        >
          <LinkedDocumentTable
            items={linkedInvoices}
            columnLabel={t.payments.detail.linkedDocuments.invoiceNumber}
            appliedAmountLabel={t.payments.detail.linkedDocuments.appliedAmount}
            appliedAtLabel={t.payments.detail.linkedDocuments.appliedAt}
            emptyStateLabel={t.payments.detail.linkedDocuments.emptyState}
            onRemove={handleRemoveFromInvoice}
            removeAriaLabel={t.payments.detail.linkedDocuments.removeInvoice}
            disabled={disabled}
          />
        </ContentSection>
      )}

      {payment && (
        <>
          <PaymentApplyToBillDialog
            isOpen={showApplyToBillDialog}
            onOpenChange={setShowApplyToBillDialog}
            paymentId={payment.id}
            unappliedAmount={payment.unappliedAmount ?? undefined}
            accountId={payment.accountId ?? undefined}
            currencyCode={payment.amount?.currencyCode}
            onSuccess={() => refetch?.()}
          />
          <PaymentApplyToInvoiceDialog
            isOpen={showApplyToInvoiceDialog}
            onOpenChange={setShowApplyToInvoiceDialog}
            paymentId={payment.id}
            unappliedAmount={payment.unappliedAmount ?? undefined}
            accountId={payment.accountId ?? undefined}
            currencyCode={payment.amount?.currencyCode}
            onSuccess={() => refetch?.()}
          />
        </>
      )}
    </FinanceDetailLayout>
  );
};
